import '../style/LiveMenu.css';
import React, { useEffect, useState, useCallback } from 'react';
import {
  RefreshCw, Utensils, CheckCircle,
  AlertCircle, Search, ChevronDown, WifiOff, LayoutGrid
} from 'lucide-react';
import { host } from '../script/variables';
import CombosOffers from './CombosOffers';

const getToken = () => localStorage.getItem('partnertoken');
const fmtTime  = (d) =>
  d?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

/* ─── Veg / Non-veg dot ─── */
const VegDot = ({ type }) => {
  const mod = type === 'nonveg' ? 'vd--nv' : type === 'mixed' ? 'vd--mix' : '';
  return (
    <span className={`vd ${mod}`} title={type === 'nonveg' ? 'Non-veg' : type === 'mixed' ? 'Egg' : 'Veg'}>
      <span className="vd__i" />
    </span>
  );
};

/* ─── Toggle ─── */
const Toggle = ({ on, loading, onChange }) => (
  <button
    type="button"
    className={`lm-tog ${on ? 'lm-tog--on' : 'lm-tog--off'}`}
    onClick={onChange}
    disabled={loading}
    aria-pressed={on}
    title={on ? 'Mark as Out of Stock' : 'Mark as Available'}
  >
    <span className="lm-tog__thumb">
      <span className="lm-tog__s" />
    </span>
  </button>
);

/* ─── Dish Card — 3-col grid card ─── */
const DishCard = ({ dish, mealType, categoryName, cafeid, onRefresh, onError }) => {
  const [busy, setBusy] = useState(false);

  // Only isAvailable (manual toggle) — no inventory `available` field
  const isOn  = dish.isAvailable !== false;
  const name  = dish.name?.trim() || '(Unnamed dish)';
  const sizes = ['small', 'medium', 'large'].filter(s => dish[s]?.price);

  const toggle = async () => {
    if (!cafeid || busy) return;
    setBusy(true);
    onError('');
    try {
      const r = await fetch(`${host}/recipe/toggleDishAvailability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getToken() },
        body: JSON.stringify({
          cafeid,
          dishId: dish._id,
          isAvailable: !isOn,
        }),
      });
      const d = await r.json();
      if (!r.ok || d.success === false) {
        onError(d.msg || 'Could not update — please try again');
        return;
      }
      await onRefresh();
    } catch {
      onError('Network error — please check your connection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`lm-dish ${!isOn ? 'lm-dish--oos' : ''}`}>
      {/* Top row: veg dot + toggle */}
      <div className="lm-dish__top">
        <VegDot type={dish.type} />
        <Toggle on={isOn} loading={busy} onChange={toggle} />
      </div>

      {/* Dish name */}
      <p className="lm-dish__name" title={name}>{name}</p>

      {/* Bottom: status + prices */}
      <div className="lm-dish__foot">
        {isOn ? (
          <span className="lm-pill-status lm-pill-status--live"><CheckCircle /> Live</span>
        ) : (
          <span className="lm-pill-status lm-pill-status--oos"><AlertCircle /> Out of Stock</span>
        )}
        {sizes.length > 0 && (
          <div className="lm-dish__sizes">
            {sizes.map(s => (
              <span key={s} className="lm-size">
                <span className="lm-size__l">{s[0]}</span>
                <span className="lm-size__p">₹{dish[s].price}</span>
                {dish[s].discount > 0 && <span className="lm-size__d">{dish[s].discount}%</span>}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Category ─── */
const Category = ({ cat, mealType, cafeid, onRefresh, onError, search }) => {
  const [open, setOpen] = useState(true);

  const dishes = (cat.dishes || []).filter(d =>
    !search || (d.name || '').toLowerCase().includes(search.toLowerCase())
  );
  if (dishes.length === 0) return null;

  const liveN = dishes.filter(d => d.isAvailable !== false).length;
  const badge =
    liveN === dishes.length ? 'lm-cat__badge--all'
  : liveN === 0             ? 'lm-cat__badge--none'
  :                           'lm-cat__badge--partial';

  return (
    <div className="lm-cat">
      <div
        className={`lm-cat__head ${open ? 'open' : ''}`}
        onClick={() => setOpen(o => !o)}
        role="button"
        aria-expanded={open}
      >
        <div className="lm-cat__left">
          <span className="lm-cat__name">{cat.name}</span>
          <span className={`lm-cat__badge ${badge}`}>{liveN}/{dishes.length} live</span>
        </div>
        <ChevronDown className={`lm-cat__chevron ${open ? 'open' : ''}`} />
      </div>

      {open && (
        <div className="lm-cat__grid">
          {dishes.map((dish, i) => (
            <DishCard
              key={i}
              dish={dish}
              mealType={mealType}
              categoryName={cat.name}
              cafeid={cafeid}
              onRefresh={onRefresh}
              onError={onError}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Meal Section ─── */
const MealSection = ({ section, cafeid, onRefresh, onError, search }) => {
  const label = section.type
    ? section.type.charAt(0).toUpperCase() + section.type.slice(1)
    : 'Menu';

  const all   = section.categories.flatMap(c => c.dishes || []);
  const liveN = all.filter(d => d.isAvailable !== false).length;

  const hasMatch = section.categories.some(c =>
    (c.dishes || []).some(d =>
      !search || (d.name || '').toLowerCase().includes(search.toLowerCase())
    )
  );
  if (!hasMatch) return null;

  return (
    <section className="lm-meal" id={`m-${section.type}`}>
      <div className="lm-meal__header">
        <h2 className="lm-meal__title">{label}</h2>
        <div className="lm-meal__rule" />
        <span className="lm-meal__ratio">{liveN}/{all.length}</span>
      </div>
      {section.categories.map((cat, i) => (
        <Category
          key={i}
          cat={cat}
          mealType={section.type}
          cafeid={cafeid}
          onRefresh={onRefresh}
          onError={onError}
          search={search}
        />
      ))}
    </section>
  );
};

/* ══════════════════════════════════════
   ROOT
══════════════════════════════════════ */
const LiveMenu = ({ cafeid }) => {
  const [menuData, setMenuData] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [ts,       setTs]       = useState(null);
  const [search,   setSearch]   = useState('');
  const [stuck,    setStuck]    = useState(false);

  const fetchMenu = useCallback(async () => {
    if (!cafeid) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${host}/order/liveMenu?cafeid=${cafeid}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (!r.ok) { setError('Failed to load — tap Refresh'); return; }
      setMenuData(await r.json());
      setTs(new Date());
    } catch {
      setError('No connection. Please check your internet.');
    } finally {
      setLoading(false);
    }
  }, [cafeid]);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  useEffect(() => {
    const h = () => setStuck(window.scrollY > 4);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const menu = (menuData?.menu || [])
    .filter(m => m?.categories?.some(c => c?.dishes?.length > 0))
    .map(m => ({ ...m, categories: m.categories.filter(c => c?.dishes?.length > 0) }));

  const allDishes   = menu.flatMap(m => m.categories.flatMap(c => c.dishes || []));
  const totalLive   = allDishes.filter(d => d.isAvailable !== false).length;
  const totalDishes = allDishes.length;

  const jumpTo = (type) => {
    const el = document.getElementById(`m-${type}`);
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 140, behavior: 'smooth' });
  };

  if (loading && !menuData) return (
    <div className="lm">
      <div className="lm-center">
        <div className="lm-spin-lg" />
        <p className="lm-muted">Loading your menu…</p>
      </div>
    </div>
  );

  if (error && !menuData) return (
    <div className="lm">
      <div className="lm-center">
        <WifiOff size={40} strokeWidth={1.5} color="#EF4444" />
        <p className="lm-muted">{error}</p>
        <button className="lm-btn-retry" onClick={fetchMenu}>Try Again</button>
      </div>
    </div>
  );

  return (

    <>
    
    <CombosOffers cafeid={cafeid}  />
    
    
    <div className="lm">

      <header className="lm-header">
        <div className="lm-header__row">
          <div className="lm-brand">
            <div className="lm-logo"><Utensils /></div>
            <div>
              <p className="lm-brand__name">{'Live Menu'}</p>
              <p className="lm-brand__sub">Manage what customers see right now</p>
            </div>
          </div>
          <div className="lm-header__right">
            {ts && <span className="lm-ts">Updated {fmtTime(ts)}</span>}
            <button className="lm-btn-refresh" onClick={fetchMenu} disabled={loading}>
              <RefreshCw className={loading ? 'spinning' : ''} />
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </header>

      <div className="lm-stats">
        <div className="lm-stat">
          <span className="lm-stat__n lm-stat__n--live">{totalLive}</span>
          <span className="lm-stat__l">Live Now</span>
        </div>
        <div className="lm-stat">
          <span className="lm-stat__n lm-stat__n--hidden">{totalDishes - totalLive}</span>
          <span className="lm-stat__l">Out of Stock</span>
        </div>
        <div className="lm-stat">
          <span className="lm-stat__n lm-stat__n--total">{totalDishes}</span>
          <span className="lm-stat__l">Total Dishes</span>
        </div>
        <div className={`lm-store ${totalLive > 0 ? 'lm-store--open' : 'lm-store--closed'}`}>
          <span className={`lm-store__dot ${totalLive > 0 ? 'lm-store__dot--open' : 'lm-store__dot--closed'}`} />
          <span className={`lm-store__text ${totalLive > 0 ? 'lm-store__text--open' : 'lm-store__text--closed'}`}>
            {totalLive > 0 ? 'Store Open' : 'Nothing Live'}
          </span>
        </div>
      </div>

      <div className={`lm-controls ${stuck ? 'stuck' : ''}`}>
        <div className="lm-search-wrap">
          <Search />
          <input
            className="lm-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search dishes…"
            autoCorrect="off"
            autoCapitalize="none"
          />
        </div>
        {menu.length > 1 && (
          <div className="lm-pills">
            {menu.map((m, i) => {
              const label = m.type ? m.type.charAt(0).toUpperCase() + m.type.slice(1) : 'Menu';
              return (
                <button key={i} className="lm-pill" onClick={() => jumpTo(m.type)}>{label}</button>
              );
            })}
          </div>
        )}
      </div>

      {error && menuData && (
        <div className="lm-toast">
          <AlertCircle />
          <span>{error}</span>
          <button className="lm-toast__x" onClick={() => setError('')}>✕</button>
        </div>
      )}

      <main className="lm-content">
        {menu.length === 0 ? (
          <div className="lm-empty">
            <div className="lm-empty__icon"><LayoutGrid /></div>
            <p className="lm-empty__title">No menu configured</p>
            <p className="lm-empty__sub">Add dishes from the menu builder to get started.</p>
          </div>
        ) : (
          menu.map((section, i) => (
            <MealSection
              key={i}
              section={section}
              cafeid={cafeid}
              onRefresh={fetchMenu}
              onError={setError}
              search={search}
            />
          ))
        )}
      </main>

    </div>

    </>
  );
};

export default LiveMenu;